/**
 * Tu año (puro): ahorro acumulado frente al objetivo y frente al mismo periodo del año anterior, ahorro y liquidez
 * mes a mes, gastos por categoría y mes (con lo raro marcado), tus notas y lo fuera de lo normal sin explicar.
 *
 * «Lo normal» de cada celda es el mismo que en Inicio y en la página de categoría: la media de los 12 meses cerrados
 * anteriores a ese mes, desde que existe el gasto (nunca depende de meses posteriores). «Media/mes» es solo descriptiva.
 */
import { monthTotals, round2, SAVINGS_GOAL, type Attributed } from '@pf/domain';
import type { ArchivedMovement } from '@pf/workbook';
import { effectiveSnapshots, type Computed } from './model.js';
import { categoryInsights } from './insights.js';

/** Cuánto por encima de lo normal se colorea: +25 % («algo más»), +75 % («bastante más»), y al menos 20 € de diferencia. */
export const DEVIATION = { mild: 1.25, strong: 1.75, minEuros: 20 };
export type Level = '' | 'dev1' | 'dev2' | 'dev3';

/** Color de una celda: ⚠ (dev3) es la misma regla que los avisos de Inicio (el doble y 100 € más). */
export function levelOf(x: { amount: number; normal: number | null; unusual: boolean }): Level {
  if (x.unusual) return 'dev3';
  if (!x.normal || x.amount <= x.normal * DEVIATION.mild || x.amount - x.normal < DEVIATION.minEuros) return '';
  return x.amount >= x.normal * DEVIATION.strong ? 'dev2' : 'dev1';
}

export interface YearCell { month: string; amount: number; normal: number | null; unusual: boolean; level: Level; note: string; open: boolean; /** pagado de gastos previstos */ planned: number }
export interface YearModel {
  year: number; months: string[]; current: string;
  /** Los 12 meses del año (los posteriores al actual, en el año en curso, aún no han llegado). */
  allMonths: string[];
  saved: number; income: number; rate: number | null;
  goalToDate: number | null; goalYear: number | null;
  lastYear: { saved: number; rate: number | null } | null;
  perMonth: Array<{ month: string; income: number; expenses: number; savings: number; live: boolean; cash: number | null }>;
  categories: Array<{ category: string; fixed: boolean; total: number; /** media de los meses cerrados del año desde que existe */ avg: number | null; cells: YearCell[] }>;
  /** Total de gastos de cada mes (todas las categorías). */
  monthTotals: number[];
  /** Tus notas, por mes, con el importe y lo normal de su celda. */
  notes: Array<{ month: string; category: string; note: string; amount: number; normal: number | null }>;
  /** Gastos fuera de lo normal (⚠) que aún no tienen nota. */
  unexplained: Array<{ month: string; category: string; amount: number; normal: number | null }>;
}

export function yearModel(c: Computed, archive: ArchivedMovement[], year: number, today: string): YearModel {
  const current = today.slice(0, 7);
  const allMonths = Array.from({ length: 12 }, (_, i) => `${year}-${String(i + 1).padStart(2, '0')}`);
  const months = allMonths.filter(m => m <= current);
  const per = months.map(m => ({ month: m, ...monthTotals(c.items, m) }));
  const income = round2(per.reduce((t, p) => t + p.income, 0)), saved = round2(per.reduce((t, p) => t + p.savings, 0));
  const goalRow = c.wb.budget.find(b => b.year === year && b.category === SAVINGS_GOAL);
  const goalOf = (i: number) => (goalRow ? goalRow.overrides[i] ?? goalRow.monthly : 0);
  // Mismo periodo del año anterior: con lo archivado (y lo que quede de ese año en Movimientos)
  const prevMonths = months.map(m => `${year - 1}${m.slice(4)}`);
  const prevItems: Attributed[] = [...archive.map(a => ({ category: a.category, month: a.month, kind: a.kind, attributable: a.attributable })), ...c.items];
  const prev = prevMonths.map(m => monthTotals(prevItems, m));
  const prevIncome = prev.reduce((t, p) => t + p.income, 0), prevSaved = round2(prev.reduce((t, p) => t + p.savings, 0));
  const cash = new Map(effectiveSnapshots(c).map(sn => [sn.month, sn.cash]));
  // Lo normal de cada categoría en cada mes y si ese mes fue raro: lo mismo que en Inicio
  const insightBy = new Map<string, Map<string, { normal: number | null; unusual: boolean; planned: number }>>();
  months.forEach(m => insightBy.set(m, new Map(categoryInsights(c, m).map(x => [x.category, { normal: x.normal, unusual: x.unusual, planned: x.planned }]))));
  const yearNotes = c.wb.notes.filter(n => n.month.startsWith(`${year}-`));
  const noteOf = (m: string, cat: string) => yearNotes.find(n => n.month === m && n.category.toLowerCase() === cat.toLowerCase())?.note ?? '';
  const byCat = new Map<string, Map<string, number>>();
  const first = new Map<string, string>();
  for (const it of c.items) {
    if (it.kind !== 'expense' || !it.month) continue;
    if (!first.has(it.category) || it.month < first.get(it.category)!) first.set(it.category, it.month);
    if (!months.includes(it.month)) continue;
    const row = byCat.get(it.category) ?? new Map<string, number>();
    row.set(it.month, (row.get(it.month) ?? 0) - it.attributable);
    byCat.set(it.category, row);
  }
  const fixedCats = new Set(c.wb.categories.filter(x => x.nature === 'fixed').map(x => x.name.toLowerCase()));
  const categories = [...byCat.entries()].map(([category, row]) => {
    const total = round2([...row.values()].reduce((t, v) => t + v, 0));
    // Media/mes: meses cerrados del año desde que existe el gasto (el mes en curso no cuenta: está a medias)
    const closed = months.filter(m => m < current && m >= (first.get(category) ?? m));
    const avg = closed.length ? round2(closed.reduce((t, m) => t + (row.get(m) ?? 0), 0) / closed.length) : null;
    return { category, fixed: fixedCats.has(category.toLowerCase()), total, avg,
      cells: months.map(m => {
        const ins = insightBy.get(m)?.get(category);
        const cell = { month: m, amount: round2(row.get(m) ?? 0), normal: ins?.normal ?? null, unusual: !!ins?.unusual, note: noteOf(m, category), open: m === current, planned: ins?.planned ?? 0 };
        // Lo que es pago de un gasto previsto no colorea: se compara con lo normal el resto
        return { ...cell, level: levelOf({ ...cell, amount: round2(cell.amount - cell.planned) }) };
      }) };
  }).filter(r => Math.abs(r.total) >= 0.005).sort((a, b) => b.total - a.total);
  const cellOf = (m: string, cat: string) => categories.find(r => r.category.toLowerCase() === cat.toLowerCase())?.cells.find(x => x.month === m);
  const notes = yearNotes.map(n => { const x = cellOf(n.month, n.category); return { month: n.month, category: n.category, note: n.note, amount: x?.amount ?? 0, normal: x?.normal ?? null }; })
    .sort((a, b) => a.month.localeCompare(b.month) || a.category.localeCompare(b.category, 'es'));
  const unexplained = categories.flatMap(r => r.cells.filter(x => x.unusual && !x.note).map(x => ({ month: x.month, category: r.category, amount: x.amount, normal: x.normal })))
    .sort((a, b) => a.month.localeCompare(b.month) || b.amount - a.amount);
  return {
    year, months, current, allMonths, saved, income, rate: income > 0 ? Math.round(saved / income * 1000) / 10 : null,
    goalToDate: goalRow ? round2(months.reduce((t, _m, i) => t + goalOf(i), 0)) : null,
    goalYear: goalRow ? round2(Array.from({ length: 12 }, (_, i) => goalOf(i)).reduce((t, v) => t + v, 0)) : null,
    lastYear: prevIncome > 0 ? { saved: prevSaved, rate: Math.round(prevSaved / prevIncome * 1000) / 10 } : null,
    perMonth: per.map(p => ({ month: p.month, income: p.income, expenses: p.expenses, savings: p.savings, live: p.month === current, cash: cash.get(p.month) ?? null })),
    categories, monthTotals: months.map(m => round2(-per.find(p => p.month === m)!.expenses)), notes, unexplained,
  };
}
